import { TradingProcedureResponseDto, TradingProcedureTypeDto, TradingProcedureStatusDto, CurrentOrganizationOfferResponseDto, TradingProcedureRegistryInfoResponseDto, OfferResultResponseDto } from '@tradgine/data-access/tradgine-data';
import { TradingProcedure, Auction, AutoBid, TradingProcedureType, TradingProcedureStatus, CurrentOrganizationOffer, Lot, TradingProcedureRegistry, OfferResult } from '@tradgine/application-english-auction-trade';
import { AuctionResponseDto } from '@tradgine/data-access/auction-flow';
import { AutoBidInstructionResponseDto } from '@tradgine/data-access/auto-bid';
import { normalizeCurrency } from '../../../../core/constants/currency-map';
import { calculateIterationFromAmount, exhaustiveCheck } from '@tradgine/utils';

export function toTradingProcedure(dto: TradingProcedureResponseDto): TradingProcedure {
    const type = toType(dto.type);

    return {
        id: dto.id,
        type,
        showSellerName: dto.showSellerName,
        name: dto.name,
        sellerOrganizationName: dto.sellerOrganizationName,
        sellerOrganizationLogo: dto.sellerOrganizationLogo,
        sellerOrganizationTypeName: dto.sellerOrganizationTypeName,
        sellerOrganizationId: dto.sellerOrganizationId,
        startDateTime: new Date(dto.startDateTime),
        endDateTime: new Date(dto.endDateTime),
        auctionExtensionSeconds: dto.auctionExtensionSeconds,
        status: toTradingProcedureStatus(dto.status),
        bidStepAmount: dto.bidStepAmount,
        sellerDescription: dto.sellerDescription,
        platformNote: dto.platformNote,
        currency: normalizeCurrency(dto.currency),
        createdDateTime: new Date(dto.createdDateTime),

        lotCount: dto.lotCount,
        contractCount: dto.contractCount,
        debtAmount: dto.debtAmount,
        principalAmount: dto.principalAmount,
        averageDpd: dto.averageDpd,
        minPrice: dto.minPrice,
        currentPrice: dto.currentPrice,
    };
}

export function toAuction(dto: AuctionResponseDto): Auction {
    return {
        id: dto.id,
        lotId: dto.id,
        status: dto.status === 'Active' ? 'active' : 'finished',
        currentIteration: dto.currentIteration,
        endDateTime: new Date(dto.endDateTime),
        leadingOrganizationId: dto.leadingOrganizationId
    }
}

export function toAutoBid(autobid: AutoBidInstructionResponseDto): AutoBid {
    return {
        id: autobid.id,
        lotId: autobid.lotId,
        organizationId: autobid.organizationId,
        targetIteration: autobid.targetIteration,
        createdDateTime: new Date(autobid.createdDateTime),
    };
}

export function toCurrentOrganizationOffer(
    dto: CurrentOrganizationOfferResponseDto,
    lot: Lot,
    tradingProcedure: TradingProcedure
): CurrentOrganizationOffer {
    const iteration = calculateIterationFromAmount(
        dto.amount,
        lot.minPrice,
        tradingProcedure.bidStepAmount
    );

    return {
        lotId: dto.lotId,
        amount: dto.amount,
        iteration: iteration,
    };
}

export function toOfferResult(dto: OfferResultResponseDto): OfferResult {
    return {
        id: dto.id,
        lotId: dto.lotId,
        organizationId: dto.organizationId,
        amount: dto.amount,
        postedDateTime: dto.postedDateTime,
    };
}

export function toTradingProcedureRegistry(dto: TradingProcedureRegistryInfoResponseDto): TradingProcedureRegistry {
    return {
        tradingProcedureId: dto.tradingProcedureId,
        fileName: dto.fileName,
        extension: dto.extension,
        description: dto.description,
        contentLength: dto.contentLength,
        createdDateTime: dto.createdDateTime,
    };
}

export function toTradingProcedureStatus(dto: TradingProcedureStatusDto): TradingProcedureStatus {
    switch (dto) {
        case TradingProcedureStatusDto.Draft:            return 'Draft';
        case TradingProcedureStatusDto.Published:        return 'Published';
        case TradingProcedureStatusDto.Active:           return 'Active';
        case TradingProcedureStatusDto.AwaitingDecision: return 'AwaitingDecision';
        case TradingProcedureStatusDto.Finished:         return 'Finished';
        case TradingProcedureStatusDto.Cancelled:        return 'Cancelled';
        default:                                         return exhaustiveCheck(dto);
    }
}

function toType(type: TradingProcedureTypeDto): TradingProcedureType {
    switch (type) {
        case TradingProcedureTypeDto.English:
            return TradingProcedureType.English;
        case TradingProcedureTypeDto.Sealed:
            return TradingProcedureType.Sealed;
        default:
            return TradingProcedureType.Unknown;
    }
}
